<template>
  <!-- when loading -->
  <ul
      v-if="isLoading"
      class="flex w-full flex-nowrap overflow-hidden items-center gap-2"
  >
    <li v-for="(_, index) in Array.from({ length: 12 })" :key="index">
      <div
          class="flex store-item animate-pulse bg-white flex-col p-3 w-[150px] mx-0.5 sm:mx-1 md:w-[157px] text-center items-center justify-center"
          role="status"
      >
        <div
            class="w-[80px] image-container bg-gray-200 rounded-full dark:bg-gray-700 mb-4 select-none h-[80px] lg:w-[85px] lg:h-[85px] overflow-hidden"
        />
        <span
            class="text-[13px] bg-gray-200 h-[15px] w-[70%] font-bold mt-0 mb-2 text-[#000000]"
        />
      </div>
    </li>
  </ul>
  <!-- when loaded -->
  <div v-else class="w-full">
    <div class="custom-slider" ref="sliderContainer"
      :data-paused="isPaused"
      :data-dragging="isDragging"
      @mouseenter="pauseAutoplay"
      @mouseleave="resumeAutoplay">
      <div 
        class="slider-track" 
        ref="sliderTrack" 
        :style="trackStyle"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @mousedown="handleTouchStart"
        @mousemove="handleTouchMove"
        @mouseup="handleTouchEnd"
        @mouseleave="handleTouchEnd"
      >
        <div 
          class="slider-item" 
          v-for="(category, index) in duplicatedCategoriesList" 
          :key="`${category.id}-${index}`"
          :style="{ width: itemWidth + 'px' }"
        >
          <CategoryItem :asLink="true" :category="category" :nameKey="nameKey"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {lowerString} from "@/helpers";
import defaultImg from "@/assets/icons/cezma_default_icon.png";
import CategoryItem from "../CategoryItem.vue";

export default {
  setup() {
    const usedCookieLanguage = useCookie('localStorageCopy')?.value?.language || 'ar';
    return {
      usedCookieLanguage,
    };
  },
  data() {
    return {
      defaultImg,
      currentSlidePerView: 8,
      allowSwiperToRender: true,
      itemWidth: 157,
      currentPosition: 0,
      animationFrameId: null,
      lastTimestamp: null,
      scrollSpeed: 20, // pixels per second
      isDragging: false,
      startX: 0,
      startPosition: 0,
      isPaused: false,
      touchTimeout: null,
    };
  },
  components: {
    CategoryItem,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    categoriesList: {
      type: Array,
      required: true,
      default: () => [],
    },
    nameKey: {
      type: String,
      required: true,
      default: 'name_ar'
    },
  },
  computed: {
    isRightToLeft() {
      return lowerString(this.currentlySelectedLanguage || this.usedCookieLanguage) == "ar";
    },
    duplicatedCategoriesList() {
      // Create multiple copies of the categories list for seamless infinite looping
      if (!this.categoriesList || this.categoriesList.length === 0) return [];
      
      // Duplicate the list 6 times to ensure smooth infinite looping
      const duplicated = [];
      for (let i = 0; i < 6; i++) {
        duplicated.push(...this.categoriesList);
      }
      return duplicated;
    },
    trackStyle() {
      const direction = this.isRightToLeft ? 1 : -1;
      // Round position to avoid sub-pixel rendering issues
      const roundedPosition = Math.round(this.currentPosition * direction * 100) / 100;
      return {
        // Use translate3d for better GPU acceleration
        transform: `translate3d(${roundedPosition}px, 0, 0)`,
        // Remove transition during animation - only use when dragging ends
        transition: this.isDragging ? 'none' : 'none'
      };
    }
  },
  methods: {
    startAutoplay() {
      this.stopAutoplay();
      this.lastTimestamp = performance.now();
      this.animate();
    },
    stopAutoplay() {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.lastTimestamp = null;
    },
    animate() {
      if (this.isPaused || this.isDragging) {
        this.lastTimestamp = null;
        return;
      }

      const currentTimestamp = performance.now();
      const deltaTime = this.lastTimestamp ? currentTimestamp - this.lastTimestamp : 16; // Default to ~60fps if no last timestamp
      this.lastTimestamp = currentTimestamp;

      // Calculate movement based on time delta for consistent speed across all frame rates
      const movement = (this.scrollSpeed * deltaTime) / 1000;
      this.currentPosition += movement;

      // Check for loop reset - cache calculations to avoid repeated work
      const totalWidth = this.duplicatedCategoriesList.length * this.itemWidth;
      const visibleWidth = this.currentSlidePerView * this.itemWidth;
      const maxPosition = totalWidth - visibleWidth;
      
      if (this.currentPosition >= maxPosition) {
        this.currentPosition = 0;
      }

      // Continue animation loop - use direct function reference for better performance
      this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
    },
    moveNext() {
      const totalWidth = this.duplicatedCategoriesList.length * this.itemWidth;
      const visibleWidth = this.currentSlidePerView * this.itemWidth;
      
      // Move very slowly for smooth continuous movement
      this.currentPosition += 1;
      
      // Reset to beginning when reaching the end
      if (this.currentPosition >= totalWidth - visibleWidth) {
        this.currentPosition = 0;
      }
    },
    movePrev() {
      this.currentPosition -= this.itemWidth;
      
      // Reset to end when reaching the beginning
      if (this.currentPosition < 0) {
        const totalWidth = this.duplicatedCategoriesList.length * this.itemWidth;
        const visibleWidth = this.currentSlidePerView * this.itemWidth;
        this.currentPosition = totalWidth - visibleWidth;
      }
    },
    handleTouchStart(e) {
      // Don't prevent default on touchstart to allow clicks to work
      this.isDragging = false; // Start as false, will be set to true on movement
      this.startX = e.touches ? e.touches[0].clientX : e.clientX;
      this.startPosition = this.currentPosition;
      this.stopAutoplay();
      
      // Add a small delay to distinguish between taps and swipes
      this.touchTimeout = setTimeout(() => {
        this.isDragging = true;
      }, 150);
    },
    handleTouchMove(e) {
      if (!this.isDragging) return;
      
      // Only prevent default if we're actually dragging (significant movement)
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      const diff = Math.abs(this.startX - currentX);
      
      // If movement is minimal, don't prevent default to allow clicks
      if (diff < 10) return;
      
      e.preventDefault();
      const direction = this.isRightToLeft ? -1 : 1;
      
      // Calculate new position with resistance for better feel
      const resistance = 0.8;
      this.currentPosition = this.startPosition + ((this.startX - currentX) * direction * resistance);
    },
    handleTouchEnd(e) {
      // Clear the touch timeout
      if (this.touchTimeout) {
        clearTimeout(this.touchTimeout);
        this.touchTimeout = null;
      }
      
      if (!this.isDragging) return;
      
      // Don't prevent default to allow clicks to work
      this.isDragging = false;
      
      // Snap to nearest item with smooth transition
      const snappedPosition = Math.round(this.currentPosition / this.itemWidth) * this.itemWidth;
      
      // Apply smooth transition for snapping
      const track = this.$refs.sliderTrack;
      if (track) {
        track.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        this.currentPosition = snappedPosition;
        // Remove transition after animation completes
        setTimeout(() => {
          if (track) {
            track.style.transition = 'none';
          }
        }, 300);
      } else {
        this.currentPosition = snappedPosition;
      }
      
      // Restart autoplay after a delay to allow user interaction
      setTimeout(() => {
        this.startAutoplay();
      }, 1000);
    },
    calculateSlidesPerView() {
      setTimeout(() => {
        const itemFullWidth = 120;
        const itemFullWidthOnMobile = 100;
        const slideWidth = window.innerWidth >= 480 ? itemFullWidth : itemFullWidthOnMobile;
        const containerWidth = this.$refs.sliderContainer?.getBoundingClientRect()?.width;

        if (containerWidth) {
          this.currentSlidePerView = Math.floor(containerWidth / slideWidth);
          this.itemWidth = slideWidth;
        }
      }, 10);
    },
    async forceRerenderSwiper() {
      this.allowSwiperToRender = false;
      await nextTick();
      this.allowSwiperToRender = true;
      await nextTick();
      this.calculateSlidesPerView();
    },
    pauseAutoplay() {
      this.stopAutoplay();
      // Add visual feedback that slider is paused
      this.isPaused = true;
    },
    resumeAutoplay() {
      // Only resume if not currently dragging
      if (!this.isDragging) {
        this.isPaused = false;
        // Restart animation if it was stopped
        if (!this.animationFrameId) {
          this.startAutoplay();
        }
      }
    },
  },
  watch: {
    isRightToLeft() {
      this.forceRerenderSwiper();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateSlidesPerView();
      this.startAutoplay();
    });
    window.addEventListener('resize', this.calculateSlidesPerView);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateSlidesPerView);
    this.stopAutoplay();
    
    // Clear touch timeout
    if (this.touchTimeout) {
      clearTimeout(this.touchTimeout);
      this.touchTimeout = null;
    }
  }
};
</script>

<style scoped>
.custom-slider {
  overflow: hidden;
  position: relative;
  width: 100%;
  /* Add smooth transition for hover effects */
  transition: all 0.3s ease;
  cursor: grab;
}

.custom-slider:hover {
  /* Subtle visual feedback when hovering */
  cursor: grab;
}

.custom-slider:active {
  cursor: grabbing;
}

.slider-track {
  display: flex;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* GPU acceleration for smoother movement */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

.slider-track:active {
  cursor: grabbing;
}

.slider-item {
  flex-shrink: 0;
  padding: 0 4px;
  /* Add smooth transitions for hover effects */
  transition: all 0.2s ease;
  pointer-events: auto;
  /* Isolate repaints to prevent flickering */
  contain: layout style paint;
  /* GPU acceleration */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

/* Enhanced hover effects for category cards - disabled during dragging */
.custom-slider:not([data-dragging="true"]) .slider-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

/* Disable hover effects when slider is actively moving */
.custom-slider[data-dragging="true"] .slider-item:hover {
  transform: none;
  box-shadow: none;
}

/* Touch interaction improvements */
.slider-track {
  touch-action: pan-x;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

/* RTL Support */
.custom-slider[data-rtl="true"] .slider-track {
  direction: rtl;
}

/* Smooth transitions - removed during animation for better performance */
.slider-track {
  /* No transition during animation - handled by requestAnimationFrame */
  transition: none;
  transform-style: preserve-3d;
  /* Remove backface-visibility: hidden to prevent flickering */
  backface-visibility: visible;
  -webkit-backface-visibility: visible;
}

/* Only apply will-change when actively animating */
.custom-slider:not([data-paused="true"]):not([data-dragging="true"]) .slider-track {
  will-change: transform;
}

/* Remove will-change when paused or dragging to optimize performance */
.custom-slider[data-paused="true"] .slider-track,
.custom-slider[data-dragging="true"] .slider-track {
  will-change: auto;
}

/* Ensure smooth infinite loop */
.slider-track {
  animation: none;
  transform-origin: center center;
}

/* Pause indicator - subtle visual feedback */
.custom-slider[data-paused="true"] .slider-track {
  opacity: 0.95;
}

/* Manual swipe feedback */
.custom-slider[data-dragging="true"] .slider-track {
  transition: none;
  cursor: grabbing;
}

/* Touch feedback for mobile */
@media (hover: none) and (pointer: coarse) {
  .slider-track {
    cursor: grab;
  }
  
  .slider-track:active {
    cursor: grabbing;
  }
}

/* Mobile-specific improvements for touchpad/touch */
@media (max-width: 1024px) {
  .slider-item {
    /* Ensure touch target is large enough */
    min-height: 44px;
    min-width: 44px;
    
    /* Improve touch interaction */
    -webkit-tap-highlight-color: rgba(0,0,0,0.1);
    touch-action: manipulation;
    pointer-events: auto;
    
    /* Better visual feedback */
    transition: all 0.15s ease;
  }
  
  .slider-item:active {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }
  
  /* Ensure category items are easily clickable */
  .slider-item .category__slider__item {
    cursor: pointer;
    pointer-events: auto;
    z-index: 10;
    position: relative;
  }
  
  /* Improve touch area for category images and text */
  .slider-item .image-container {
    pointer-events: auto;
    cursor: pointer;
    /* Optimize image rendering */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
  
  .slider-item .category_name {
    pointer-events: auto;
    cursor: pointer;
    -webkit-tap-highlight-color: rgba(0,0,0,0.1);
  }
}
</style>

